import { Injectable, NgZone } from '@angular/core';
import { GlobalPosition } from '../models/global-position.model';
import { NotificationService } from './notification.service';
import { LoggingService } from './logging.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  watchId: number | null = null;

  constructor(private notification: NotificationService,
    private loggingService: LoggingService,
    private zone: NgZone) { }

  createPositionStream(): Observable<GeolocationPosition> {
    return new Observable<GeolocationPosition>((observer) => {
      if (!('geolocation' in navigator)) {
        observer.error('Geolocation not supported');
        return;
      }

      // Avoid duplicate watchers
      if (this.watchId === null) {
        this.watchId = navigator.geolocation.watchPosition(
          (pos) => {
            this.zone.run(() => observer.next(pos));  // push into Angular zone
          },
          (err) => {
            this.zone.run(() => {
              switch (err.code) {
                case err.PERMISSION_DENIED:
                  this.notification.dangerToast(`You are not able to check in because you have not given permission for your location to be used. ${err.code, err.message}`);
                  break;
                case err.POSITION_UNAVAILABLE:
                  this.notification.dangerToast(`Position unavailable ${err.code, err.message}`);
                  break;
                case err.TIMEOUT:
                  this.notification.dangerToast(`Request timed out ${err.code, err.message}`);
                  break;
                default:
                  return this.notification.dangerToast("Unknown geolocation error.");
              }
            });
          },
          {
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 60000
          }
        );
      }

      // Cleanup
      return () => {
        if (this.watchId !== null) {
          navigator.geolocation.clearWatch(this.watchId);
          this.watchId = null;
        }
      };
    });
  }

  private getCurrentPosition(buyerName: string, options = {}, timeout = 20000) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const errorMessage = {
          message: `Geolocation request timed out - no response from user ${buyerName}.`,
          stack: JSON.stringify(`Geolocation request timed out - no response from user ${buyerName}.`)
        };

        this.loggingService.logError(errorMessage);
      }, timeout);

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimeout(timeoutId);
          resolve(pos);
        },
        (err) => {
          clearTimeout(timeoutId);
          reject(err);
        },
        options
      );
    });
  }

  async getGeolocation(buyerName: string): Promise<GlobalPosition | null> {
    let coords = null;
    await this.getCurrentPosition(buyerName).then((position: GeolocationPosition) => {
      this.loggingService.logInfo(`Location allowed for ${buyerName}`);
      coords = new GlobalPosition(position.coords.latitude, position.coords.longitude);
    }).catch((error: GeolocationPositionError) => {
      let message: string = 'Unknown error, try again!';
      switch (error?.code) {
        case error.PERMISSION_DENIED:
          if (error?.message.startsWith('User denied Geolocation')) {
            this.notification.dangerToast(error?.message);
          } 
          break;
        case error.POSITION_UNAVAILABLE:
          message = `Hardware error / cannot determine ${error?.code, error?.message}, browser: ${navigator.userAgent}`;
          this.notification.dangerToast(message);
          break;
        case error.TIMEOUT:
          message = `Geolocation information was not obtained in the allowed time. ${error?.code, error?.message},  browser: ${navigator.userAgent}`;
          this.notification.dangerToast(message);
          break;
        default:
          message = `Unknown geolocation error ${error?.message},  browser: ${navigator.userAgent}`;
          this.notification.dangerToast(message);
      }
    });
    return coords;
  }
}
